/* eslint-disable @next/next/no-img-element */
import {
  SeasonFragment,
  SeasonLeagueFragment,
  SeasonStatsPlayerFragment,
} from '@amf/shared/types/graphql';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import Container from '@amf/shared/components/layout/Container';
import Link from 'next/link';
import Loader from '@amf/shared/components/utils/Loader';
import Empty from '@amf/shared/components/utils/Empty';
import useLeagueStats from '@amf/shared/data/hooks/useLeagueStats';
import SeasonPlayerStatsFilter from '@amf/shared/components/stats/SeasonPlayerStatsFilter';

enum StatsType {
  assists = 'assists',
  goals = 'goals',
  points = 'points',
}

interface SeasonPlayerStatsTableProps {
  season: SeasonFragment;
  league: SeasonLeagueFragment;
  disableAssists?: boolean;
  disablePoints?: boolean;
  prefixes: {
    player: string;
    team: string;
  };
}

function SeasonPlayerStatsTable({
  season,
  league,
  disableAssists,
  disablePoints,
  prefixes,
}: SeasonPlayerStatsTableProps) {
  const { t } = useTranslate();
  const { stats, loading } = useLeagueStats(season.id, league.id, null);
  const [type, setType] = useState<StatsType>(StatsType.goals);

  const players = useMemo<SeasonStatsPlayerFragment[]>(() => {
    if (!stats) return [];
    if (type === StatsType.goals) return stats.goalPlayers;
    if (type === StatsType.points) return stats.pointPlayers;
    if (type === StatsType.assists) return stats.assistPlayers;
    return [];
  }, [stats, type]);

  return (
    <Container>
      <SeasonPlayerStatsFilter
        onChange={setType}
        disableAssists={disableAssists}
        disablePoints={disablePoints}
      />
      {loading && <Loader>{t('loading')}</Loader>}
      {!loading && players.length === 0 && <Empty>{t('stats.empty')}</Empty>}
      {!loading && players.length > 0 && (
        <div className='SeasonPlayerStatsTable'>
          <table className='SeasonPlayerStatsTable__table'>
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>{t('stats.grid.player')}</th>
                <th>{t('stats.grid.team')}</th>
                <th>{t(`stats.grid.${type}`)}</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, order) => (
                <tr key={player.id}>
                  <td width={16}>
                    <div className='SeasonPlayerStatsTable__order'>{order + 1}</div>
                  </td>
                  <td>
                    <Link href={`${prefixes.player}/${player.player.id}`}>
                      <a className='SeasonPlayerStatsTable__cell SeasonPlayerStatsTable__cell--horizontal'>
                        {player.player.photoUrl && (
                          <img
                            className='SeasonPlayerStatsTable__avatar'
                            src={player.player.photoUrl}
                            alt={player.player.fullName}
                          />
                        )}
                        {!player.player.photoUrl && (
                          <div className='SeasonPlayerStatsTable__avatar' />
                        )}
                        <strong>{player.player.fullName}</strong>
                      </a>
                    </Link>
                  </td>
                  <td>
                    {player.player.currentTeam && (
                      <Link href={`${prefixes.team}/${player.player.currentTeam.id}`}>
                        <a className='SeasonPlayerStatsTable__cell'>
                          <div className='SeasonPlayerStatsTable__title'>
                            {player.player.currentTeam.name}
                          </div>
                          <div className='SeasonPlayerStatsTable__subtitle'>
                            {player.player.currentTeam.club.name}
                          </div>
                        </a>
                      </Link>
                    )}
                  </td>
                  <td>
                    <div className='SeasonPlayerStatsTable__cell'>
                      <div>{player.count}</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
}

export default SeasonPlayerStatsTable;
